// src/pages/admin/AdminStatsPage.tsx - Manual overrides for the nflverse stats pipeline:
// re-run ingestion for a season/week and rebuild the player ID map. The daily sweep
// already does this automatically — these are backfill/repair tools.
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import BackLink from '../../components/common/BackLink';

interface IngestResult {
  message: string;
  kickers_stored?: number;
  punters_stored?: number;
  updated?: number;
  skipped?: number;
  unmatched?: number;
  [key: string]: unknown;
}

interface IdMapResult {
  message: string;
  matched: number;
  persisted: number;
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-secondary-500 text-xs uppercase tracking-wide">{label}</dt>
      <dd className="font-display text-lg text-ink-900">{value}</dd>
    </div>
  );
}

export default function AdminStatsPage() {
  const { user } = useAuth();

  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [week, setWeek] = useState('');

  const [ingesting, setIngesting] = useState(false);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [ingestResult, setIngestResult] = useState<IngestResult | null>(null);

  const [mapping, setMapping] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapResult, setMapResult] = useState<IdMapResult | null>(null);

  if (!user?.is_superuser) {
    return <Navigate to="/app" replace />;
  }

  const seasonNum = Number(season);
  const seasonValid = season.trim() !== '' && !Number.isNaN(seasonNum);
  const weekNum = week.trim() === '' ? undefined : Number(week);

  const runIngest = async () => {
    if (!seasonValid) return;
    setIngesting(true);
    setIngestError(null);
    setIngestResult(null);
    try {
      const result = await apiService.ingestStats(seasonNum, weekNum);
      setIngestResult(result);
    } catch (e: any) {
      setIngestError(e.message || 'Ingest failed');
    } finally {
      setIngesting(false);
    }
  };

  const runBuildIdMap = async () => {
    if (!seasonValid) return;
    setMapping(true);
    setMapError(null);
    setMapResult(null);
    try {
      const result = await apiService.buildIdMap(seasonNum);
      setMapResult(result);
    } catch (e: any) {
      setMapError(e.message || 'Build failed');
    } finally {
      setMapping(false);
    }
  };

  return (
    <div>
      <BackLink to="/app/admin">Back to Admin Panel</BackLink>
      <div className="border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Stats / Ingest</h1>
        <p className="text-secondary-600 mt-1">
          The daily sweep already ingests stats automatically. These are manual overrides and
          backfill tools — use them to re-run a season or week, or repair the player ID map.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="stats-season" className="form-label">
            Season
          </label>
          <input
            id="stats-season"
            type="number"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="form-input w-28"
          />
        </div>
        <div>
          <label htmlFor="stats-week" className="form-label">
            Week (optional)
          </label>
          <input
            id="stats-week"
            type="number"
            value={week}
            onChange={(e) => setWeek(e.target.value)}
            placeholder="Full season"
            className="form-input w-28"
          />
        </div>
      </div>

      <section className="mb-8 border border-secondary-300 bg-white p-6">
        <h2 className="font-display uppercase text-ink-900 text-xl leading-none mb-3">
          Ingest Stats
        </h2>
        <p className="text-sm text-secondary-600 mb-4">
          Pulls stats from nflverse for the season above. Leave week blank to ingest the full
          season (weeks 1-18).
        </p>
        <button
          type="button"
          disabled={!seasonValid || ingesting}
          onClick={runIngest}
          className="btn px-3 py-1.5 text-xs font-semibold uppercase tracking-wider bg-ink-900 text-paper hover:bg-ink-900/90 disabled:opacity-50"
        >
          {ingesting ? 'Ingesting…' : 'Ingest Stats'}
        </button>

        {ingestError && (
          <p className="mt-3 text-sm text-danger" role="alert">
            {ingestError}
          </p>
        )}

        {ingestResult && (
          <div className="mt-4 border border-secondary-200 bg-secondary-50 p-4 text-sm">
            <p className="font-medium text-ink-900">{ingestResult.message}</p>
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
              {ingestResult.kickers_stored !== undefined && (
                <StatTile label="Kickers Stored" value={ingestResult.kickers_stored} />
              )}
              {ingestResult.punters_stored !== undefined && (
                <StatTile label="Punters Stored" value={ingestResult.punters_stored} />
              )}
              {ingestResult.updated !== undefined && (
                <StatTile label="Updated" value={ingestResult.updated} />
              )}
              {ingestResult.skipped !== undefined && (
                <StatTile label="Skipped" value={ingestResult.skipped} />
              )}
              {ingestResult.unmatched !== undefined && (
                <StatTile label="Unmatched" value={ingestResult.unmatched} />
              )}
            </dl>
          </div>
        )}
      </section>

      <section className="border border-secondary-300 bg-white p-6">
        <h2 className="font-display uppercase text-ink-900 text-xl leading-none mb-3">
          Build ID Map
        </h2>
        <p className="text-sm text-secondary-600 mb-4">
          Rebuilds and persists the nflverse player ID mapping for the season above.
        </p>
        <button
          type="button"
          disabled={!seasonValid || mapping}
          onClick={runBuildIdMap}
          className="btn px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper disabled:opacity-50"
        >
          {mapping ? 'Building…' : 'Build ID Map'}
        </button>

        {mapError && (
          <p className="mt-3 text-sm text-danger" role="alert">
            {mapError}
          </p>
        )}

        {mapResult && (
          <div className="mt-4 border border-secondary-200 bg-secondary-50 p-4 text-sm">
            <p className="font-medium text-ink-900">{mapResult.message}</p>
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
              <StatTile label="Matched" value={mapResult.matched} />
              <StatTile label="Persisted" value={mapResult.persisted} />
            </dl>
          </div>
        )}
      </section>
    </div>
  );
}
